import { CAIP2, Chain, Currency, Hex, Session } from "@paywithglide/glide-js";
import { createContext, useContext } from "react";
import { FundingSource } from "./fundingSource";
import { Theme } from "./theme";

export type GlideDepositConfigContextType = {
  appName: string;
  appLogoUrl: string;
  appFaviconUrl: string;
  chain?: Chain;
  currency?: Currency;
  mode?: "deposit" | "withdraw" | "pay" | "call" | "buy" | "refund";
  amount?: string;
  amountUSD?: string;
  hasFixedAmount: boolean;
  recipient?: string;
  evmTx?: {
    chainId: number;
    to: Hex;
    data?: Hex;
    value?: bigint;
  };
  tokenApproval?: {
    token: Hex;
    amount: bigint;
  };
  canSwitchMode?: boolean;

  gasRefuelAmountPerChain: Record<string, string> | null;
  preferGaslessPayment?: boolean;
  chainIds?: CAIP2[];
  excludeChainIds?: CAIP2[];
  excludeCurrencyTiers?: ("tier1" | "tier2" | "tier3")[];
  excludeFundingSources?: FundingSource[];
  allowedPaymentCurrencies?: string[];
  excludedPaymentCurrencies?: string[];
  defaultPaymentCurrency?: string;

  embed?: boolean;
  autoCloseOnSuccess?: boolean;
  connectedWalletAddress?: string | null;

  glideConfig: {
    projectId: string;
    chains: Chain[];
  };

  withdrawChains?: Chain[];

  defaultDepositTokenDenomination?: "token" | "usd";

  paymentMethods: Array<
    | "wallet"
    | "external_wallet"
    | "transfer"
    | "coinbase"
    | "coinbase_app"
    | "interac"
    | "fiat"
    | "debit_card_alias"
    | "onramp"
    | "onramper"
  >;

  chooseCurrencyFirst?: boolean;

  disableWithdrawToSelfSuggestion?: boolean;

  commissionRates?: Record<"tier1" | "tier2" | "tier3", string>;

  sessionMetadata?: string;
  whitelabel?: boolean;
  widgetSessionId?: string;
  glideSessionId?: string;
  refundSession?: Session;
  refundToken?: string;

  didProvideLocalStorage: boolean;

  setMode: (newMode: "deposit" | "withdraw") => void;
  setRecipient: (newRecipient: Hex) => void;
  setChain: (newChain: Chain) => void;
  setCurrency: (newCurrency: Currency) => void;

  debug?: boolean;
  popupsBlocked?: boolean;
  showSupportedTokensMessage?: boolean;
  hideSettlementCopy?: boolean;
  tokenAmountDefault?: boolean;
  showAppLogo: "default" | "always" | "never";
  showOnrampFirst?: boolean;
  debitCardAlias?: "coinbase" | "fiat";
  copyOverrides?: Record<string, string>;

  enableRefundEmails?: boolean;
  payerEmail?: string;
  userPhoneNumber?: string;
  stableDepositAddressKey?: string;
  safeInsetBottom: number;

  widgetConfig: {
    appMetadata: {
      id: string;
      name?: string;
      logoUrl?: string;
      faviconUrl?: string;
    };
    theme?: Partial<Theme>;
  };
};

export const GlideDepositConfigContext = createContext<
  GlideDepositConfigContextType | undefined
>(undefined);

export const useGlideDepositConfig = () => {
  const context = useContext(GlideDepositConfigContext);
  if (!context) {
    throw new Error(
      "useGlideDepositConfig must be used within a GlideDepositConfigProvider",
    );
  }
  return context;
};
